import React, { FunctionComponent, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import Link from 'core/routing/link';
import Button from 'ui/atoms/button';
import ConnectedInvestmentsTable from './connected-investments';
import VoucherModal from '../vouchers/create-voucher';
import { useVoucherDetails } from './hooks';
import LoadingRing from 'ui/atoms/loading-ring';
import Translate from 'ui/atoms/translate';
import Section from 'ui/atoms/section';
import BoxedContent from 'ui/molecules/boxed-content';
import StatusTag from 'ui/atoms/status-tag';
import Date from 'ui/atoms/date';
import CopyButton from 'ui/molecules/copy-button';
import ActionButtons from 'ui/molecules/action-buttons';
import Currency from 'ui/atoms/currency';
import CardItem from 'src/ui/atoms/card-item';
import { toMoney } from 'src/ui/helper/money';

interface IssuerVoucherDetailsPageProps {
  voucherId: string;
}

const IssuerVoucherDetailsPage: FunctionComponent<IssuerVoucherDetailsPageProps> = ({ voucherId }) => {
  const [isEditVoucherModalOpen, setIsEditVoucherModalOpen] = useState(false);
  const [refreshVoucherDetails, setRefreshVoucherDetails] = useState(false);

  const { voucher, steps, changeVoucherActivation, loading } = useVoucherDetails(voucherId, refreshVoucherDetails);

  if (!voucherId || !voucher || loading)
    return (
      <WideContent title={<Translate name="dashboardIssuerVoucherDetails.title" args={[voucher?.name || '']} />}>
        <LoadingRing />
      </WideContent>
    );

  return (
    <>
      <VoucherModal
        isEditMode={true}
        isOpen={isEditVoucherModalOpen}
        createdSteps={steps}
        voucher={voucher}
        onModalClose={() => setIsEditVoucherModalOpen(false)}
        onVoucherChanged={() => {
          setRefreshVoucherDetails(false);
          setRefreshVoucherDetails(true);
          setIsEditVoucherModalOpen(false);
        }}
      />
      <Section>
        <WideContent
          title={<Translate name="dashboardIssuerVoucherDetails.title" args={[voucher.name]} />}
          titleSpacing="larger"
        >
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
            <CardItem
              title={<Translate name="voucherCode.voucherCode" />}
              content={voucher.code}
              icon={<CopyButton valueToCopy={voucher.code} />}
            />
            <CardItem
              title={<Translate name="issuerProductDetails.campaigns.campaignDetails.createdAt" />}
              content={<Date>{voucher.createdAt}</Date>}
            />
            <CardItem
              title={<Translate name="common.status" />}
              content={
                <StatusTag variant={voucher.active ? 'success' : 'danger'} truncate={true}>
                  <Translate
                    name={`issuerProductDetails.campaigns.status.${voucher.active ? 'active' : 'deactivated'}`}
                  />
                </StatusTag>
              }
            />
            <CardItem
              title={<Translate name="dashboardIssuerVoucherDetails.voucherSteps" />}
              content={
                steps.length > 0 &&
                steps.map((step, index) => (
                  <Section spacing="tiny">
                    <Translate
                      key={index}
                      name="dashboardIssuerVoucherDetails.voucherStepDetails"
                      args={[
                        <Currency>{step.minimumInvestmentAmount}</Currency>,
                        step.disagioPercent ? (
                          <>{Number(step.disagioPercent)}%</>
                        ) : (
                          <Currency>{toMoney(step.disagio || 0)}</Currency>
                        ),
                      ]}
                    />
                  </Section>
                ))
              }
            />
            <CardItem
              title={<Translate name="dashboardIssuerVoucherDetails.validity" />}
              content={
                voucher.productNames && voucher.productNames.length > 0 ? (
                  voucher.productNames.map((product) => <Section spacing="tiny">{product}</Section>)
                ) : (
                  <Translate name="createVoucherForm.validity.allProducts" />
                )
              }
            />
            <CardItem
              title={<Translate name="createVoucherForm.numberOfUsesTotal.label" />}
              content={
                voucher.numberOfUses === 0 ? <Translate name="createVoucherForm.unlimited" /> : voucher.numberOfUses
              }
            />
            <CardItem
              title={<Translate name="createVoucherForm.numberOfUsesByInvestor.label" />}
              content={
                voucher.firstTimeOnly ? (
                  <Translate name="dashboardIssuerVoucherDetails.firstTime" />
                ) : (
                  voucher.numberOfUsesInvestor
                )
              }
            />
            <ActionButtons>
              <Button variant="link" onClick={changeVoucherActivation}>
                <Translate
                  name={`dashboardIssuerVoucherDetails.${voucher.active ? 'deactivateVoucher' : 'activateVoucher'}`}
                />
              </Button>
              <Button variant="secondary" onClick={() => setIsEditVoucherModalOpen(true)}>
                <Translate name="common.edit" />
              </Button>
            </ActionButtons>
          </BoxedContent>
        </WideContent>
      </Section>

      <WideContent
        title={<Translate name="dashboardIssuerVoucherDetails.connectedInvestments" />}
        ignorePageTitle
        titleSpacing="medium"
      >
        <ConnectedInvestmentsTable voucherId={voucherId} defaultLimit={50} />
      </WideContent>

      <WideContent>
        <ActionButtons align="start">
          <Link icon="arrow-left" to="/dashboard/vouchers">
            <Translate as="span" name="dashboardIssuerVoucherDetails.goToVouchers" />
          </Link>
        </ActionButtons>
      </WideContent>
    </>
  );
};

export default WithAuthenticatedPage(IssuerVoucherDetailsPage, AppType.ISSUER);
